import { calculate } from "@netkeep/fee-engine";
import type { CalculationInput, CalculationResult, FeeSchedule } from "@netkeep/fee-types";
import {
  type CalculatorFormParsed,
  type CalculatorFormValues,
  calculatorFormSchema,
  formToCalculationInput,
} from "@netkeep/validation";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { type UseFormReturn, useForm, useWatch } from "react-hook-form";
import { DEFAULT_CURRENCY } from "@/lib/config";
import { useFeeData } from "./fee-data";

export const CUSTOM_PROFILE_ID = "custom";

export const EMPTY_FORM: CalculatorFormValues = {
  salePrice: "",
  shippingCharged: "",
  taxMode: "NONE",
  taxRate: "",
  taxAmount: "",
  taxIncludesShipping: true,
  itemCost: "",
  shippingCost: "",
  packagingCost: "",
  otherExpenses: "",
  subscriptionOverhead: "",
  promotedListingPercent: "",
  marketplacePercent: "",
  marketplaceFixed: "",
  paymentPercent: "",
  paymentFixed: "",
};

export interface Computation {
  input: CalculationInput | null;
  result: CalculationResult | null;
  /** Field name → message for invalid inputs. */
  fieldErrors: Partial<Record<keyof CalculatorFormValues, string>>;
  /** Engine-level error, e.g. an invalid fee profile. */
  error: string | null;
}

interface CalculatorState {
  form: UseFormReturn<CalculatorFormValues, unknown, CalculatorFormParsed>;
  profileId: string;
  profile: FeeSchedule | null;
  setProfileId: (id: string) => void;
  computation: Computation;
  load: (values: CalculatorFormValues, profileId: string) => void;
  reset: () => void;
}

const CalculatorContext = createContext<CalculatorState | null>(null);

/** Runs validation + the fee engine for a set of form values. Pure and synchronous. */
export function compute(values: CalculatorFormValues, profile: FeeSchedule | null): Computation {
  const parsed = calculatorFormSchema.safeParse(values);
  if (!parsed.success) {
    const fieldErrors: Computation["fieldErrors"] = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as keyof CalculatorFormValues | undefined;
      if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
    }
    return { input: null, result: null, fieldErrors, error: null };
  }
  const input = formToCalculationInput(parsed.data, profile?.currency ?? DEFAULT_CURRENCY);
  try {
    return { input, result: calculate(input, profile), fieldErrors: {}, error: null };
  } catch (err) {
    return { input, result: null, fieldErrors: {}, error: err instanceof Error ? err.message : String(err) };
  }
}

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const { schedules } = useFeeData();
  const form = useForm<CalculatorFormValues, unknown, CalculatorFormParsed>({
    defaultValues: EMPTY_FORM,
    mode: "onChange",
  });
  const [profileId, setProfileId] = useState<string>(schedules[0]?.id ?? CUSTOM_PROFILE_ID);

  // If refreshed data no longer contains the selected schedule, fall back gracefully.
  const profile = useMemo(() => schedules.find((s) => s.id === profileId) ?? null, [schedules, profileId]);
  useEffect(() => {
    if (profileId !== CUSTOM_PROFILE_ID && !profile) setProfileId(schedules[0]?.id ?? CUSTOM_PROFILE_ID);
  }, [profile, profileId, schedules]);

  const values = useWatch({ control: form.control }) as CalculatorFormValues;
  const computation = useMemo(() => compute({ ...EMPTY_FORM, ...values }, profile), [values, profile]);

  const load = useCallback(
    (next: CalculatorFormValues, nextProfileId: string) => {
      form.reset({ ...EMPTY_FORM, ...next });
      setProfileId(nextProfileId);
    },
    [form],
  );
  const reset = useCallback(() => form.reset(EMPTY_FORM), [form]);

  const value = useMemo(
    () => ({ form, profileId, profile, setProfileId, computation, load, reset }),
    [form, profileId, profile, computation, load, reset],
  );
  return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
}

export function useCalculator(): CalculatorState {
  const ctx = useContext(CalculatorContext);
  if (!ctx) throw new Error("useCalculator must be used inside CalculatorProvider");
  return ctx;
}
